import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useEvalRoleLabels } from '../../contexts/EvalRoleLabelsContext';
import { batchLabel, useBatches } from '../../hooks/useBatches';

interface QualityTemplate {
  id: number;
  pool: string;
  relationWeights: Record<string, number>;
}

interface PoolRelationWeightsModalProps {
  open: boolean;
  pool: { code: string; displayName: string } | null;
  onClose: () => void;
}

type WeightRow = { role: string; weight: number };

export function PoolRelationWeightsModal({ open, pool, onClose }: PoolRelationWeightsModalProps) {
  const qc = useQueryClient();
  const { enabledCodes, getEvalRoleLabel } = useEvalRoleLabels();
  const { batches, batchId, setBatchId, isLoading: batchesLoading } = useBatches();
  const [rows, setRows] = useState<WeightRow[]>([]);
  const [addRole, setAddRole] = useState<string | null>(null);

  const { data: templates = [], isFetching: templatesLoading } = useQuery<QualityTemplate[]>({
    queryKey: ['quality-templates', batchId],
    queryFn: () => api.get('/admin/quality-templates', { params: { batchId } }),
    enabled: open && !!batchId,
  });

  const template = useMemo(() => {
    if (!pool) return null;
    const matched = templates.filter((t) => t.pool === pool.code);
    if (!matched.length) return null;
    return matched.reduce((best, cur) => (cur.id > best.id ? cur : best));
  }, [templates, pool]);

  useEffect(() => {
    if (!open || !pool) return;
    if (!template) {
      setRows([]);
      return;
    }
    setRows(
      Object.entries(template.relationWeights || {}).map(([role, weight]) => ({
        role,
        weight: Number(weight) || 0,
      })),
    );
    setAddRole(null);
  }, [open, pool, template]);

  const ensureMut = useMutation({
    mutationFn: (vars: { batchId: number; pool: string }) =>
      api.post('/admin/quality-templates/ensure-missing', vars),
    onSuccess: () => {
      message.success('已初始化该人才池维度模板');
      qc.invalidateQueries({ queryKey: ['quality-templates'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || '初始化失败'),
  });

  const saveMut = useMutation({
    mutationFn: (vars: { id: number; relationWeights: Record<string, number> }) =>
      api.patch(`/admin/quality-templates/${vars.id}`, { relationWeights: vars.relationWeights }),
    onSuccess: () => {
      message.success('评价角色权重已保存');
      qc.invalidateQueries({ queryKey: ['quality-templates'] });
      onClose();
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || '保存失败'),
  });

  const sum = useMemo(() => rows.reduce((acc, r) => acc + (Number(r.weight) || 0), 0), [rows]);
  const sumValid = Math.abs(sum - 100) <= 0.01;
  const usedRoles = useMemo(() => new Set(rows.map((r) => r.role)), [rows]);
  const addableRoles = enabledCodes.filter((c) => !usedRoles.has(c));

  const handleSave = () => {
    if (!template) {
      message.warning('请先初始化该人才池模板');
      return;
    }
    if (!rows.length) {
      message.warning('请至少保留一个评价角色');
      return;
    }
    if (!sumValid) {
      message.warning('评价人权重合计必须恰好等于 100%');
      return;
    }
    const relationWeights: Record<string, number> = {};
    for (const row of rows) relationWeights[row.role] = Number(row.weight) || 0;
    saveMut.mutate({ id: template.id, relationWeights });
  };

  return (
    <Modal
      title={pool ? `评价角色权重 · ${pool.displayName}` : '评价角色权重'}
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            disabled={!template || !sumValid || !rows.length}
            loading={saveMut.isPending}
            onClick={handleSave}
          >
            保存
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <Space wrap align="center">
          <Typography.Text type="secondary">盘点批次</Typography.Text>
          <Select
            style={{ minWidth: 220 }}
            loading={batchesLoading}
            value={batchId}
            options={batches.map((b) => ({
              value: b.id,
              label: `${batchLabel(b)}${b.phase === 'CLOSED' ? '（已关闭）' : ''}`,
            }))}
            onChange={(id) => setBatchId(id)}
          />
          <Tag color={sumValid ? 'success' : 'error'}>合计 {Number(sum.toFixed(2))}%</Tag>
        </Space>

        <Alert
          type="info"
          showIcon
          message="本表只改当前批次该人才池模板的评价角色与权重；删角色后评价关系列/组织同步不再带它，已派任务不会自动删除。"
        />

        {!templatesLoading && pool && !template && (
          <Alert
            type="warning"
            showIcon
            message="当前批次还没有该人才池的维度模板"
            action={
              <Button
                size="small"
                type="primary"
                loading={ensureMut.isPending}
                disabled={!batchId}
                onClick={() => batchId && pool && ensureMut.mutate({ batchId, pool: pool.code })}
              >
                立即初始化
              </Button>
            }
          />
        )}

        <Space wrap>
          <Select
            placeholder="从字典添加角色"
            style={{ width: 220 }}
            disabled={!template || addableRoles.length === 0}
            value={addRole}
            options={addableRoles.map((c) => ({
              value: c,
              label: `${getEvalRoleLabel(pool?.code, c)}（${c}）`,
            }))}
            onChange={(code) => {
              if (!code) return;
              setRows((prev) => [...prev, { role: code, weight: 0 }]);
              setAddRole(null);
            }}
            allowClear
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <PlusOutlined /> 添加后默认权重 0%，需把合计调到 100% 再保存
          </Typography.Text>
        </Space>

        <Table<WeightRow>
          rowKey="role"
          size="small"
          loading={templatesLoading}
          pagination={false}
          dataSource={rows}
          locale={{ emptyText: template ? '暂无评价角色，请从上方添加' : '请先初始化模板' }}
          columns={[
            {
              title: '评价角色',
              dataIndex: 'role',
              render: (role: string) => getEvalRoleLabel(pool?.code, role),
            },
            {
              title: 'Code',
              dataIndex: 'role',
              width: 140,
              render: (role: string) => <Tag>{role}</Tag>,
            },
            {
              title: '权重 %',
              dataIndex: 'weight',
              width: 140,
              render: (weight: number, record) => (
                <InputNumber
                  min={0}
                  max={100}
                  value={weight}
                  style={{ width: 100 }}
                  disabled={!template}
                  onChange={(v) =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.role === record.role ? { ...r, weight: Number(v ?? 0) } : r,
                      ),
                    )
                  }
                />
              ),
            },
            {
              title: '操作',
              key: 'op',
              width: 90,
              render: (_v, record) => (
                <Popconfirm
                  title={`移除「${getEvalRoleLabel(pool?.code, record.role)}」？`}
                  description="保存后本池不再使用该角色。"
                  okText="移除"
                  cancelText="取消"
                  disabled={!template}
                  onConfirm={() => setRows((prev) => prev.filter((r) => r.role !== record.role))}
                >
                  <Button type="link" danger size="small" icon={<DeleteOutlined />} disabled={!template}>
                    删除
                  </Button>
                </Popconfirm>
              ),
            },
          ]}
        />

        {!sumValid && rows.length > 0 && (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            合计必须恰好等于 100% 才能保存
          </Typography.Text>
        )}
      </Space>
    </Modal>
  );
}
